import { IconButton, Modal, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { formatVideoName } from "../hooks/useFetchVideos";
import { IconGeo, IconPage, IconSettings, IconShare, IconSparkles, IconTag } from "../../../../utils/icons";
import { useUser } from "../../../../hooks/useUser";
import { isInternal } from "../../../../utils";
import { VideoAutopilotPanel } from "./VideoAutopilotPanel";
import { SettingsForm } from "./SettingsForm";
import { IBrand } from "../../../brand/team/data/type";
import { ShareVideoModal } from "./ShareVideoModal";
import { TabHeader, TabPanel } from "../../../../components/Tabs";
import { BetaPill } from "../../../../components/BetaPill";
import { SeoAioVideoPanel } from "./SeoAioVideoPanel";
import { TranscriptionVideoPanel } from "./TranscriptionVideoPanel";
import { VideoTagsPanel } from "./VideoTagsPanel";
import { FilterChip } from "../../../../components/videoSection/FilterChip";
import axiosInstant from "../../../../api";
import { getVideoTags } from "../../../../api/endpoints";

interface Props {
    video: any;
    brand: IBrand;
    onClose: () => void;
    onComplete: () => void;
}

export function SettingsVideoModal({ video, onClose, ...rest }: Props) {
    const [selectedTab, setSelectedTab] = useState(0);
    const [tagCount, setTagCount] = useState(0);
    const { role } = useUser();
    // Autopilot is internal-only for now (same gate as Product settings).
    const showAutopilot = isInternal(role);
    const showTags = isInternal(role);

    useEffect(() => {
        let cancelled = false;
        if (showTags && video?.id) {
            axiosInstant
                .get(getVideoTags(video.id))
                .then(({ data }) => {
                    if (!cancelled) setTagCount((data?.tags || []).length);
                })
                .catch(() => undefined);
        }
        return () => {
            cancelled = true;
        };
    }, [showTags, video?.id]);

    const props = {
        ...rest,
        s3id: video.id,
        openSettings: () => setSelectedTab(0)
    };

    // Product Pages (Agentic Shopping / GEO) only unlocks once the video is approved
    // (`final`) — pdp-url rows are stamped with the video they were
    // generated from, and pre-approval revisions replace the file version,
    // which would leave those stamps pointing at a dead version.
    const isApproved = !!video.final;

    const tabHeader = [
        {
            value: 0,
            text: "General",
            Icon: IconSettings
        },
        {
            value: 1,
            text: "Share",
            Icon: IconShare
        },
        {
            value: 2,
            text: "Transcript",
            Icon: IconPage
        },
        ...(showTags
            ? [
                {
                    value: 3,
                    text: "Tags",
                    Icon: IconTag,
                    badge: tagCount > 0 ? <FilterChip label={tagCount} /> : undefined
                }
            ]
            : []),
        ...(isApproved
            ? [
                {
                    value: 4,
                    text: "Product Pages",
                    Icon: IconGeo
                }
            ]
            : []),
        ...(isApproved && showAutopilot
            ? [
                {
                    value: 5,
                    text: "Autopilot",
                    Icon: IconSparkles,
                    badge: <BetaPill />
                }
            ]
            : [])
    ];

    return (
        <Modal open className="flex items-center flex-col, justify-center" onClose={onClose}>
            <div className="bg-surface rounded-xl w-full max-w-[1400px] h-full max-h-[min(900px,calc(100vh-40px))] overflow-hidden flex flex-col">
                <div className="px-6 pt-6 pb-4 flex items-center justify-between shrink-0">
                    <Typography variant="h5">Video Settings</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </div>
                <TabHeader
                    tabs={tabHeader}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    containerClassName="px-6 border-b border-border-token shrink-0"
                    variant="underline"
                />
                <div className="p-6 pb-6 relative flex-1 min-h-0">
                    <TabPanel value={selectedTab} index={0} className="h-full">
                        <SettingsForm video={video} brandId={rest.brand.id || 0} {...rest} />
                    </TabPanel>
                    <TabPanel value={selectedTab} index={1} className="h-full">
                        <ShareVideoModal {...props} />
                    </TabPanel>
                    <TabPanel value={selectedTab} index={2} className="h-full">
                        <TranscriptionVideoPanel video={video} />
                    </TabPanel>
                    {showTags && (
                        <TabPanel value={selectedTab} index={3} className="h-full">
                            <VideoTagsPanel video={video} brandId={rest.brand?.id || null} onCountChange={setTagCount} />
                        </TabPanel>
                    )}
                    {isApproved && (
                        <TabPanel value={selectedTab} index={4} className="h-full">
                            <SeoAioVideoPanel video={video} brand={rest.brand} />
                        </TabPanel>
                    )}
                    {isApproved && showAutopilot && (
                        <TabPanel value={selectedTab} index={5} className="h-full">
                            <VideoAutopilotPanel video={video} brandId={rest.brand?.id || null} />
                        </TabPanel>
                    )}
                </div>
            </div>
        </Modal>
    );
}
